import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from '../abcp-data.config';

export type NoteKindWire = 'COMMENT' | 'TODO';

/** Ein Rechteck der Fundstelle, normiert auf [0,1] der UNGEDREHTEN Seite, Ursprung oben links. */
export interface INotePdfRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** Die Fundstelle einer Markierung im Beleg — REV-21369, ADR-0021 E5. Seite 1-basiert. */
export interface INotePdfAnchor {
    page: number;
    rects: INotePdfRect[];
}

/**
 * Die zitierte Stelle (ADR-0019 E5), für Belege erweitert um die Fundstelle (ADR-0021 E5).
 *
 * ⚠️ `snippet` ist `null` bei einer Flächen-Markierung (Scan ohne Text). `pdf` fehlt bei Einträgen
 * von vor REV-21369 ganz — beides ist ein Normalfall, kein Fehler.
 */
export interface IClosingNoteAnchor {
    snippet: string | null;
    blockId: string | null;
    pdf?: INotePdfAnchor | null;
}

/** Ein Eintrag, wie `aumico-be` ihn liefert. Rohdaten — Labels baut der Presenter. */
export interface IClosingNote {
    _id: string;
    kind: NoteKindWire;
    source: 'ACCOUNT' | 'DOCUMENT' | 'MANDATE' | 'AGENT';
    accountId: string | null;
    /** Der Beleg einer Dokument-Notiz (ADR-0021 E4). Fehlt bei Einträgen von vor REV-21369. */
    documentId?: string | null;
    text: string;
    /** Die Erläuterung unter dem Titel. `null` ⇒ es gibt keine. */
    description: string | null;
    mentionUserIds: string[];
    tags: string[];
    anchor: IClosingNoteAnchor | null;
    /** ISO-8601. ⚠️ Der Server sendet NIE ein Label — formatiert wird im Client. */
    dueAt: string | null;
    assigneeId: string | null;
    doneAt: string | null;
    authorId: string;
    createdAt: string;
    replies: IClosingNoteReply[];
}

export interface IClosingNoteReply {
    id: string;
    authorId: string;
    text: string;
    mentionUserIds: string[];
    at: string;
}

export interface ICreateNoteRequest {
    kind: NoteKindWire;
    source: 'ACCOUNT' | 'DOCUMENT' | 'MANDATE' | 'AGENT';
    accountId?: string | null;
    /** Nur mit `source: 'DOCUMENT'`. Das Konto setzt dann der Server VOM BELEG (ADR-0021 E4). */
    documentId?: string | null;
    text: string;
    description?: string | null;
    mentionUserIds?: string[];
    tags: string[];
    anchor?: IClosingNoteAnchor | null;
    dueAt?: string | null;
    /** Only a Pendenz; `null` = unassigned. */
    assigneeId?: string | null;
}

/** Höchstens einer ist gesetzt; ohne Filter liest das Register. */
export interface NoteListFilter {
    accountId?: string;
    documentId?: string;
}

export interface IUpdateNoteRequest {
    text?: string;
    description?: string | null;
    mentionUserIds?: string[];
    tags?: string[];
    dueAt?: string | null;
    assigneeId?: string | null;
    /** ⚠️ `null` löst die Pendenz vom Konto. Der Server prüft, dass es zum Abschluss gehört. */
    accountId?: string | null;
}

/**
 * HTTP fuer Kommentare und Pendenzen — REV-21374, `ClosingNotesController`.
 *
 * ⚠️ **`kind` faehrt bei JEDER Leseoperation mit**, auch beim Einzel-Read. Der Server verlangt es
 * (ADR-0019 E1), und die Gegenmassnahme waere wertlos, wenn der Client ihn weglassen duerfte: die
 * beiden Register wuerden sich lautlos mischen.
 *
 * ⚠️ **EINE Tuer fuer beide Register und den Konto-Bereich** (E6). `accountId` engt zusaetzlich
 * ein; ohne ihn kommt der ganze Abschluss.
 */
@Injectable()
export class ClosingNoteService {
    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoAbcpConfigService) private readonly config: AumicoAbcpConfig,
    ) {}

    private base(projectId: string): string {
        return `${this.config.apiGateway}projects/${projectId}/notes`;
    }

    public list(projectId: string, kind: NoteKindWire, filter: NoteListFilter = {}): Observable<IClosingNote[]> {
        let params = new HttpParams().set('kind', kind);
        if (filter.accountId !== undefined) {
            params = params.set('accountId', filter.accountId);
        }
        if (filter.documentId !== undefined) {
            params = params.set('documentId', filter.documentId);
        }
        return this.http.get<IClosingNote[]>(this.base(projectId), { params });
    }

    public byId(projectId: string, kind: NoteKindWire, noteId: string): Observable<IClosingNote> {
        return this.http.get<IClosingNote>(`${this.base(projectId)}/${noteId}`, {
            params: new HttpParams().set('kind', kind),
        });
    }

    public create(projectId: string, body: ICreateNoteRequest): Observable<IClosingNote> {
        return this.http.post<IClosingNote>(this.base(projectId), body);
    }

    public update(projectId: string, noteId: string, body: IUpdateNoteRequest): Observable<IClosingNote> {
        return this.http.patch<IClosingNote>(`${this.base(projectId)}/${noteId}`, body);
    }

    public remove(projectId: string, noteId: string): Observable<{ id: string }> {
        return this.http.delete<{ id: string }>(`${this.base(projectId)}/${noteId}`);
    }

    public setDone(projectId: string, noteId: string, done: boolean): Observable<IClosingNote> {
        return this.http.patch<IClosingNote>(`${this.base(projectId)}/${noteId}/done`, { done });
    }

    public reply(
        projectId: string,
        noteId: string,
        text: string,
        mentionUserIds: string[],
    ): Observable<IClosingNote> {
        return this.http.post<IClosingNote>(`${this.base(projectId)}/${noteId}/replies`, { text, mentionUserIds });
    }
}
